import { Vector2 } from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { CONFIG } from '../../config.js';
import { GlitchController } from '../GlitchController.js';
import { MatrixScreenShader } from './MatrixScreenPass.js';

/**
 * Matrix atmosphere: bloom + composite screen treatment.
 * Composer + half-float RTs are created only when an effect is enabled (saves VRAM/RAM).
 */
export class PostProcessingPipeline {
  /**
   * @param {{
   *   renderer: THREE.WebGLRenderer;
   *   scene: THREE.Scene;
   *   camera: THREE.PerspectiveCamera;
   *   settings: import('../GraphicsSettings.js').GraphicsSettings;
   * }} options
   */
  constructor({ renderer, scene, camera, settings }) {
    this.settings = settings;
    this._renderer = renderer;
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._glitch = new GlitchController(settings, {
      minInterval: CONFIG.graphics.glitchMinInterval,
      maxInterval: CONFIG.graphics.glitchMaxInterval,
      seed: CONFIG.graphics.glitchSeed,
    });

    /** @type {EffectComposer | null} */
    this.composer = null;
    this._renderPass = null;
    this._bloomPass = null;
    this._screenPass = null;

    if (this.isActive()) {
      this._ensureComposer();
    }
  }

  /** @returns {GlitchController} */
  get glitchController() {
    return this._glitch;
  }

  isActive() {
    const s = this.settings;
    return (
      s.bloom ||
      s.scanlines ||
      s.noise ||
      s.chromaticAberration ||
      s.glitch ||
      s.vignette
    );
  }

  /** @param {number} deltaSeconds */
  update(deltaSeconds) {
    this._time += deltaSeconds;
    this._glitch.update(deltaSeconds);

    if (!this.isActive()) {
      return;
    }

    if (!this.composer) {
      this._ensureComposer();
    }

    this._syncPassFlags();

    const u = this._screenPass.uniforms;
    u.uTime.value = this._time;
    u.uScanlines.value = this.settings.scanlines ? 1 : 0;
    u.uNoise.value = this.settings.noise ? 1 : 0;
    u.uChromatic.value = this.settings.chromaticAberration ? 1 : 0;
    u.uDistortion.value = 0;
    u.uVignette.value = this.settings.vignette
      ? CONFIG.graphics.vignetteStrength
      : 0;
    u.uGlitch.value = this._glitch.strength;
    u.uFlicker.value = this._glitch.flicker;
    u.uBrightness.value = this._glitch.brightness;
  }

  /** @param {number} width @param {number} height @param {number} [pixelRatio] */
  setSize(width, height, pixelRatio) {
    if (!this.composer) return;
    this.composer.setSize(width, height);
    if (pixelRatio !== undefined) {
      this.composer.setPixelRatio(pixelRatio);
    }
    this._bloomPass.resolution.set(width, height);
    this._screenPass.uniforms.uResolution.value.set(
      width * (pixelRatio ?? 1),
      height * (pixelRatio ?? 1),
    );
  }

  render() {
    if (!this.isActive()) {
      this._renderer.render(this._scene, this._camera);
      return;
    }
    if (!this.composer) {
      this._ensureComposer();
    }
    this.composer.render();
  }

  dispose() {
    this.composer?.dispose();
    this.composer = null;
  }

  _ensureComposer() {
    if (this.composer) return;

    const size = this._renderer.getSize(new Vector2());
    this.composer = new EffectComposer(this._renderer);
    this.composer.setSize(size.x, size.y);

    this._renderPass = new RenderPass(this._scene, this._camera);
    this.composer.addPass(this._renderPass);

    const bloomCfg = CONFIG.graphics;
    const bloomScale = CONFIG.performance?.bloomResolutionScale ?? 1;
    this._bloomPass = new UnrealBloomPass(
      new Vector2(size.x * bloomScale, size.y * bloomScale),
      bloomCfg.bloomStrength,
      bloomCfg.bloomRadius,
      bloomCfg.bloomThreshold,
    );
    this.composer.addPass(this._bloomPass);

    this._screenPass = new ShaderPass(MatrixScreenShader);
    this._screenPass.renderToScreen = true;
    this.composer.addPass(this._screenPass);

    this._syncPassFlags();
  }

  _syncPassFlags() {
    if (!this._bloomPass) return;
    this._bloomPass.enabled = this.settings.bloom;
    const bloomCfg = CONFIG.graphics;
    this._bloomPass.strength = bloomCfg.bloomStrength;
    this._bloomPass.threshold = bloomCfg.bloomThreshold;
    this._bloomPass.radius = bloomCfg.bloomRadius;
  }
}
